import { useState } from "react";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

type Props = {
  onNavigate: (page: string) => void;
};

export default function Header({ onNavigate }: Props) {
  const { t } = useTranslation();

  const [active, setActive] = useState("home");
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { id: "home", label: t("nav.home") },
    { id: "about", label: t("nav.about") },
    { id: "tech", label: t("nav.tech") },
    { id: "academy", label: t("nav.academy") },
  ];

  const handleNav = (id: string) => {
    // "academy" opens an external link instead of switching internal pages
    if (id === "academy") {
      window.open("https://academy.tech-interview.com/", "_blank", "noopener,noreferrer");
      setMobileOpen(false);
      return;
    }

    setActive(id);
    onNavigate(id);
    setMobileOpen(false);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header className="sticky top-0 z-[100] w-full border-b border-slate-100 bg-white/80 backdrop-blur">
      <div className="flex items-center justify-between px-4 py-4 sm:px-6 lg:px-12">

        {/* LEFT */}
        <div className="flex items-center gap-6 lg:gap-12">

          {/* LOGO */}
          <div
            onClick={() => handleNav("home")}
            className="flex cursor-pointer items-center gap-3 group"
          >
            <div className="flex items-center">
              <div className="rounded-lg p-1 shadow-sm transition-transform group-hover:rotate-6">
                <img
                  src="https://optim.tildacdn.net/tild6335-3931-4163-b337-663330333730/-/resize/38x/-/format/webp/Vector_2.png.webp"
                  alt="GeoAlpha"
                  className="h-9 w-7 sm:h-11 sm:w-9 lg:h-12 lg:w-10"
                />
              </div>

              <div className="flex flex-col leading-none -translate-y-[3px] sm:-translate-y-[-6px]">
                <span className="text-xl sm:text-2xl lg:text-4xl font-bold text-blue-600 tracking-tight">
                  eoAlpha
                </span>

                <span className="text-[9px] sm:text-xs lg:text-sm font-medium text-blue-500 -mt-1">
                  Solutions
                </span>
              </div>
            </div>
          </div>

          {/* DESKTOP NAV */}
          <nav className="hidden [@media(min-width:1150px)]:flex gap-10 xl:gap-14 font-sans">
  {navItems.map((item) => (
    <button
      key={item.id}
      onClick={() => handleNav(item.id)}
      className={`relative font-extrabold uppercase transition-all
        text-[1rem] xl:text-[1rem] 2xl:text-[1rem]
        tracking-[0.08em]
        ${
          active === item.id
            ? "text-blue-600"
            : "text-slate-400 hover:text-slate-700"
        }
      `}
    >
      {item.label}

      {active === item.id && (
        <span className="absolute left-0 -bottom-[4px] h-[2.5px] w-full rounded bg-blue-600" />
      )}
    </button>
  ))}
</nav>
        </div>

        {/* RIGHT */}
        <div className="flex items-center gap-3 sm:gap-5">

          <LanguageSwitcher />

          <button
  onClick={() => setMobileOpen(!mobileOpen)}
  className="[@media(min-width:1150px)]:hidden p-2 rounded-xl border border-slate-200"
>
  {mobileOpen ? <X size={22} /> : <Menu size={22} />}
</button>
        </div>
      </div>

      {/* MOBILE MENU */}
      <div
  className={`[@media(min-width:1150px)]:hidden overflow-hidden transition-all duration-300 border-t border-slate-100 bg-white
    ${mobileOpen ? "max-h-[360px] opacity-100" : "max-h-0 opacity-0"}
  `}
>
        <div className="flex flex-col px-6 py-5 gap-4">

          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNav(item.id)}
              className={`text-left text-sm sm:text-base font-black uppercase tracking-wide transition
                ${
                  active === item.id
                    ? "text-blue-600"
                    : "text-slate-500"
                }
              `}
            >
              {item.label}
            </button>
          ))}

        </div>
      </div>
    </header>
  );
}